import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { portalLandingPath } from "../lib/portalContext";
import { apiLogout, apiGetPortalProfiles } from "../lib/api";

// ============================================================
// DEPRECATED: usePortalAuth.ts
// ============================================================
// This module is being phased out in favor of the new
// Google Cloud Identity auth stack:
//   - src/hooks/useAuth.ts ........ reactive auth state
//   - src/lib/auth.ts .............. role + permission helpers
//   - src/lib/api.ts ............... Cloud Run API client
//
// All functions below now delegate to the new auth system
// while maintaining backward compatibility for existing
// imports throughout the portal.
// ============================================================

import {
  portalReturnPath,
  isLoggedIn as gcIsLoggedIn,
  getStoredProfile,
  getStoredRoles,
  hasRole as gcHasRole,
  hasAnyRole as gcHasAnyRole,
  isClient as gcIsClient,
  isClinician as gcIsClinician,
  isAdmin as gcIsAdmin,
  isSuperAdmin as gcIsSuperAdmin,
  isSupervisor as gcIsSupervisor,
  isMarketing as gcIsMarketing,
  hasAdminAccess as gcHasAdminAccess,
  hasClinicalAccess as gcHasClinicalAccess,
  hasStaffAccess as gcHasStaffAccess,
  hasMarketingAccess as gcHasMarketingAccess,
  canAssignAdminRole,
  canAccessSupervisorDashboard,
  canAccessMarketingDashboard,
  performLogout,
  broadcastPortalLogout,
  isNewClient as gcIsNewClient,
  intakeIsComplete as gcIntakeIsComplete,
  areIdentityFieldsLocked as gcAreIdentityFieldsLocked,
  getUserId as gcGetUserId,
  getUserDisplayName,
} from "../lib/auth";

// --- Session check (delegates to new system) ---

export function isLoggedIn(): boolean {
  return gcIsLoggedIn();
}

export function getUserRole(): string | null {
  const roles = getStoredRoles();
  // Return the highest-privilege role as the single "primary" role
  // for backward compatibility with components that expect one role
  const priority = ["superadmin", "admin", "clinician", "client"];
  for (const r of priority) {
    if (roles.some((role) => role.name === r)) return r;
  }
  return roles[0]?.name ?? null;
}

export function hasRole(role: string): boolean {
  return gcHasRole(role);
}

export function isClient(): boolean {
  return gcIsClient();
}

export function isAdmin(): boolean {
  return gcIsAdmin();
}

export function isClinician(): boolean {
  return gcIsClinician();
}

export function isSuperAdmin(): boolean {
  return gcIsSuperAdmin();
}

export function isSupervisor(): boolean {
  return gcIsSupervisor();
}

export function isMarketing(): boolean {
  return gcIsMarketing();
}

export function hasMarketingAccess(): boolean {
  return gcHasMarketingAccess();
}

export function canAccessMarketing(): boolean {
  return canAccessMarketingDashboard();
}

export function hasAdminAccess(): boolean {
  return gcHasAdminAccess();
}

export function hasClinicalAccess(): boolean {
  return gcHasClinicalAccess();
}

export function hasStaffAccess(): boolean {
  return gcHasStaffAccess();
}

export function canAccessAdminRoute(): boolean {
  return gcHasAdminAccess();
}

export function canAssignAdmin(): boolean {
  return canAssignAdminRole();
}

export function canAccessSupervisorView(): boolean {
  return canAccessSupervisorDashboard();
}

// --- Client state helpers ---

export function isNewClient(): boolean {
  return gcIsNewClient();
}

export function intakeIsComplete(): boolean {
  return gcIntakeIsComplete();
}

export function areIdentityFieldsLocked(): boolean {
  return gcAreIdentityFieldsLocked();
}

export function lockIdentityFields(): void {
  const p = getStoredProfile();
  if (p) {
    // In the real system, identity lock is set server-side after
    // intake completion. We update the local cache optimistically.
    const updated = { ...p, identity_locked: true };
    sessionStorage.setItem("gc_user_profile", JSON.stringify(updated));
  }
}

// --- Identity helpers ---

export function getUserId(): string | null {
  return gcGetUserId();
}

export function getUserName(): string {
  return getUserDisplayName();
}

// --- Logout ---

export async function logout(): Promise<void> {
  try { await apiLogout(); }
  finally { performLogout(); broadcastPortalLogout(); }
}

// --- React hooks (redirects based on real auth) ---

export function useRedirectIfAuthenticated() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoggedIn()) return;
    const next = portalReturnPath(location.search, location.state);
    if (next) { navigate(next, { replace: true }); return; }
    if (hasAdminAccess()) {
      navigate("/admin", { replace: true });
    } else if (hasClinicalAccess()) {
      navigate("/staff", { replace: true });
    } else if (hasStaffAccess()) {
      navigate("/staff/availability", { replace: true });
    } else if (hasMarketingAccess()) {
      navigate("/marketing", { replace: true });
    } else {
      void apiGetPortalProfiles().then(response=>{if(response.data)navigate(portalLandingPath(response.data.active_profile),{replace:true});});
    }
  }, [navigate, location.search, location.state]);
}

export function useRequireAuth({
  fallback = "/clientportal",
}: {
  fallback?: string;
} = {}) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate(fallback, { replace: true });
    }
  }, [navigate, fallback]);
}

export function useRequireAdmin() {
  const navigate = useNavigate();

  useEffect(() => {
    const role = getUserRole();
    if (!role) {
      navigate("/clientportal", { replace: true });
    } else if (!canAccessAdminRoute()) {
      navigate("/clientportal/dashboard", { replace: true });
    }
    // If they have admin access, they stay on /admin — no extra redirect needed
  }, [navigate]);
}

export function useRequireClient() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn() || hasAdminAccess()) {
      navigate("/clientportal", { replace: true });
    }
  }, [navigate]);
}

export function useNewClientIntakeRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    if (isNewClient() && !intakeIsComplete()) {
      void apiGetPortalProfiles().then(response=>{if(response.data)navigate(portalLandingPath(response.data.active_profile),{replace:true});});
    }
  }, [navigate]);
}

export function useRequireSuperAdmin() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/clientportal", { replace: true });
    } else if (!isSuperAdmin()) {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);
}

export function useRequireClinician() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/clientportal", { replace: true });
    } else if (!hasClinicalAccess()) {
      navigate("/clientportal/dashboard", { replace: true });
    }
  }, [navigate]);
}

// --- Multi-role helpers (new, for real system) ---

export function getUserRoles(): string[] {
  return getStoredRoles().map((r) => r.name);
}

export function hasAnyRole(roleNames: string[]): boolean {
  return gcHasAnyRole(roleNames);
}

export function isMultiRoleUser(): boolean {
  const roles = getStoredRoles();
  return roles.length > 1;
}
